<template>
  <article class="ctc-product">
    <aside class="ctc-product__media">
      <div class="ctc-product__image-frame">
        <img
          v-if="productImage"
          :src="productImage"
          :alt="product.title"
          class="ctc-product__image"
        />
        <ctc-product-visual
          v-else
          :icon="product.visual.icon"
          :color="product.visual.color"
          size="lg"
          class="ctc-product__fallback"
        />

        <span class="ctc-product__merchant-badge">{{ merchant }}</span>
        <span v-if="discount !== null" class="ctc-product__discount">
          −{{ discount }} %
        </span>
      </div>

      <div class="ctc-product__source">
        <span>{{
          fromOpportunity ? "Source produit" : "Source analysée"
        }}</span>
        <strong>{{ fromOpportunity ? "Affilizz" : merchant }}</strong>
        <small v-if="!fromOpportunity" :title="product.sourceUrl">
          {{ product.sourceUrl }}
        </small>
        <small v-else>Opportunité synchronisée</small>
      </div>
    </aside>

    <section class="ctc-product__details">
      <header class="ctc-product__header">
        <div>
          <div class="ctc-product__eyebrow">Produit récupéré</div>
          <h3>Vérifiez les informations avant de continuer</h3>
        </div>
        <span class="ctc-product__verified">
          <span />
          Analyse terminée
        </span>
      </header>

      <div class="ctc-field ctc-field--title">
        <div class="ctc-field__label">Nom du produit</div>
        <rmr-text-field
          v-if="editable"
          :value="drafts.title"
          :counter="titleMaxLength"
          :error-messages="errors.title || []"
          :hide-details="false"
          size="md"
          aria-label="Titre du produit"
          @input="onTitleInput"
        />
        <div v-else class="ctc-field__value ctc-field__value--title">
          {{ product.title }}
        </div>
      </div>

      <div class="ctc-product__commerce-grid">
        <div class="ctc-field ctc-field--price">
          <div class="ctc-field__label">Prix actuel</div>
          <rmr-text-field
            v-if="editable"
            :value="drafts.priceCurrent"
            :suffix="priceSuffix"
            inputmode="decimal"
            :error-messages="errors.priceCurrent || []"
            :hide-details="false"
            aria-label="Prix actuel"
            @input="onPriceInput('priceCurrent', $event)"
          />
          <div v-else class="ctc-field__value ctc-field__value--price">
            {{ formatPrice(product.priceCurrent, product.currency) }}
          </div>
        </div>

        <div class="ctc-field">
          <div class="ctc-field__label">Ancien prix</div>
          <rmr-text-field
            v-if="editable"
            :value="drafts.priceOriginal"
            :suffix="priceSuffix"
            inputmode="decimal"
            placeholder="Aucun"
            :error-messages="errors.priceOriginal || []"
            :hide-details="false"
            aria-label="Ancien prix"
            @input="onPriceInput('priceOriginal', $event)"
          />
          <div v-else class="ctc-field__value">
            {{ formatPrice(product.priceOriginal, product.currency) }}
          </div>
        </div>

        <div class="ctc-field">
          <div class="ctc-field__label">Disponibilité</div>
          <rmr-select
            v-if="editable"
            :value="product.availability"
            :items="availabilityItems"
            aria-label="Disponibilité"
            @input="$emit('update-field', 'availability', $event)"
          />
          <div v-else class="ctc-field__value">
            {{ availabilityLabels[product.availability] }}
          </div>
        </div>

        <div class="ctc-field">
          <div class="ctc-field__label">Avis clients</div>
          <div v-if="hasRating" class="ctc-product__rating">
            <v-rating
              :value="product.rating"
              readonly
              dense
              half-increments
              color="#F59E0B"
              background-color="#CBD5E1"
              size="18"
              aria-label="Note du produit"
            />
            <span class="ctc-product__rating-score">{{ ratingScore }}</span>
            <span v-if="reviewCountLabel" class="ctc-product__rating-count">
              {{ reviewCountLabel }}
            </span>
          </div>
          <div v-else class="ctc-field__value ctc-field__value--missing">
            Aucune note disponible
          </div>
        </div>
      </div>

      <div class="ctc-product__features">
        <div class="ctc-field__label">Caractéristiques récupérées</div>
        <ul v-if="product.features.length">
          <li v-for="feature in product.features" :key="feature">
            <span aria-hidden="true">✓</span>
            {{ feature }}
          </li>
        </ul>
        <div v-else class="ctc-field__value ctc-field__value--missing">
          Aucune caractéristique récupérée
        </div>
      </div>
    </section>
  </article>
</template>

<script>
import { computed, reactive, watch } from "@vue/composition-api";

import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";

import { AVAILABILITY_LABELS, TITLE_MAX_LENGTH } from "../constants";
import {
  currencySymbol,
  discountPercent,
  formatPrice,
  merchantLabel,
  parsePriceInput,
} from "../ctc-model";
import CtcProductVisual from "./CtcProductVisual.vue";

const priceToInput = (value) =>
  typeof value === "number" ? String(value).replace(".", ",") : "";

export default {
  name: "CtcProductCard",
  components: {
    CtcProductVisual,
    RmrSelect,
    RmrTextField,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    editable: {
      type: Boolean,
      default: false,
    },
    fromOpportunity: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const drafts = reactive({ title: "", priceCurrent: "", priceOriginal: "" });

    const syncDrafts = () => {
      drafts.title = props.product.title;
      drafts.priceCurrent = priceToInput(props.product.priceCurrent);
      drafts.priceOriginal = priceToInput(props.product.priceOriginal);
    };
    watch(() => props.product.id, syncDrafts, { immediate: true });

    const onTitleInput = (value) => {
      drafts.title = value || "";
      emit("update-field", "title", drafts.title);
    };

    const onPriceInput = (field, value) => {
      drafts[field] = value || "";
      emit("update-field", field, parsePriceInput(drafts[field]));
    };

    return {
      availabilityItems: Object.keys(AVAILABILITY_LABELS).map((value) => ({
        value,
        text: AVAILABILITY_LABELS[value],
      })),
      availabilityLabels: AVAILABILITY_LABELS,
      discount: computed(() => discountPercent(props.product)),
      drafts,
      formatPrice,
      merchant: computed(() => merchantLabel(props.product.merchant)),
      onPriceInput,
      onTitleInput,
      // The prices are typed in the product currency; no symbol if unknown.
      priceSuffix: computed(() => currencySymbol(props.product.currency)),
      productImage: computed(() => props.product.imageUrl || ""),
      hasRating: computed(() => typeof props.product.rating === "number"),
      ratingScore: computed(() =>
        typeof props.product.rating === "number"
          ? String(props.product.rating).replace(".", ",")
          : ""
      ),
      reviewCountLabel: computed(() =>
        typeof props.product.reviewCount === "number"
          ? `${props.product.reviewCount.toLocaleString("fr-FR")} avis`
          : ""
      ),
      titleMaxLength: TITLE_MAX_LENGTH,
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-product {
  display: grid;
  grid-template-columns: minmax(250px, 310px) minmax(0, 1fr);
  gap: 26px;
  padding: 24px;
  border: 1px solid #dbe3ef;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 10px 32px rgba(15, 23, 42, 0.07);
}

.ctc-product__media {
  min-width: 0;
}

.ctc-product__image-frame {
  position: relative;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  border-radius: 16px;
  background: #f8fafc;
}

.ctc-product__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ctc-product__fallback {
  width: 100%;
  max-width: none;
  height: 100%;
}

.ctc-product__merchant-badge,
.ctc-product__discount {
  position: absolute;
  top: 12px;
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 850;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);
}

.ctc-product__merchant-badge {
  left: 12px;
  background: rgba(255, 255, 255, 0.94);
  color: #334155;
}

.ctc-product__discount {
  right: 12px;
  background: #ecfdf5;
  color: #047857;
}

.ctc-product__source {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.ctc-product__source span,
.ctc-product__source strong,
.ctc-product__source small {
  display: block;
}

.ctc-product__source span {
  color: #94a3b8;
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ctc-product__source strong {
  margin-top: 4px;
  color: #0f172a;
  font-size: 12px;
}

.ctc-product__source small {
  overflow: hidden;
  margin-top: 3px;
  color: #64748b;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ctc-product__details {
  min-width: 0;
}

.ctc-product__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.ctc-product__eyebrow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 5px;
  color: #6366f1;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ctc-product__header h3 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 850;
  line-height: 1.35;
}

.ctc-product__verified {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border-radius: 999px;
  background: #ecfdf5;
  color: #047857;
  font-size: 10px;
  font-weight: 850;
}

.ctc-product__verified span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.ctc-field {
  min-width: 0;
}

.ctc-field--title {
  margin-bottom: 16px;
}

.ctc-field__label {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 7px;
  color: #64748b;
  font-size: 10px;
  font-weight: 750;
}

.ctc-field__value {
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
}

.ctc-field__value--title {
  font-size: 18px;
}

.ctc-field__value--price {
  color: #0f172a;
  font-size: 22px;
  font-weight: 900;
}

.ctc-field__value--missing {
  color: #94a3b8;
  font-weight: 500;
  font-style: italic;
}

.ctc-product__commerce-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.ctc-product__commerce-grid .ctc-field {
  min-width: 0;
}

.ctc-product__commerce-grid .ctc-field--price .ctc-field__label {
  color: #4338ca;
}

.ctc-product ::v-deep .rmr-input .v-input__slot {
  background: #fff;
  border-radius: 12px;
}

.ctc-product ::v-deep .rmr-input fieldset {
  border-color: #dbe3ef;
}

.ctc-product ::v-deep .rmr-input:hover fieldset {
  border-color: #a5b4fc;
}

.ctc-product__rating {
  display: flex;
  min-height: 40px;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  padding: 6px 10px;
  border: 1px solid #dbe3ef;
  border-radius: 12px;
  background: #fff;
}

.ctc-product__rating ::v-deep .v-rating {
  display: inline-flex;
  gap: 0;
}

.ctc-product__rating ::v-deep .v-rating .v-icon {
  padding: 1px;
}

.ctc-product__rating-score {
  min-width: 30px;
  padding: 3px 7px;
  border-radius: 7px;
  background: #fff7ed;
  color: #b45309;
  font-size: 11px;
  font-weight: 900;
  text-align: center;
}

.ctc-product__rating-count {
  color: #64748b;
  font-size: 10px;
  font-weight: 650;
}

.ctc-product__features {
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 13px;
  background: #fff;
}

.ctc-product__features ul {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ctc-product__features li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #334155;
  font-size: 12px;
  line-height: 1.45;
}

.ctc-product__features li span {
  color: #059669;
  font-weight: 900;
}

@media (max-width: 960px) {
  .ctc-product {
    grid-template-columns: minmax(0, 1fr);
  }

  .ctc-product__media {
    display: grid;
    grid-template-columns: minmax(220px, 300px) minmax(0, 1fr);
    gap: 16px;
  }

  .ctc-product__source {
    align-self: end;
    margin-top: 0;
  }
}

@media (max-width: 620px) {
  .ctc-product {
    gap: 20px;
    padding: 16px;
  }

  .ctc-product__media {
    grid-template-columns: minmax(0, 1fr);
  }

  .ctc-product__source {
    margin-top: 0;
  }

  .ctc-product__header {
    flex-direction: column;
  }

  .ctc-product__commerce-grid,
  .ctc-product__features ul {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
